// Asset Track 360 - Shared Types
// These types are shared across all services and apps

// ============================================================
// Enums
// ============================================================

export enum AssetStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  MAINTENANCE = 'MAINTENANCE',
  DISPOSED = 'DISPOSED',
  IN_TRANSIT = 'IN_TRANSIT',
}

export enum AssetCondition {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  FAIR = 'FAIR',
  POOR = 'POOR',
}

export enum MovementType {
  TRANSFER = 'TRANSFER',
  LOAN = 'LOAN',
  RETURN = 'RETURN',
  MAINTENANCE_OUT = 'MAINTENANCE_OUT',
  MAINTENANCE_IN = 'MAINTENANCE_IN',
  DISPOSAL = 'DISPOSAL',
  INITIAL = 'INITIAL',
  ADJUSTMENT = 'ADJUSTMENT',
}

export enum CaptureMethod {
  RFID_PORTAL = 'RFID_PORTAL',
  RFID_MOBILE = 'RFID_MOBILE',
  MANUAL = 'MANUAL',
  QR_SCAN = 'QR_SCAN',
  BARCODE = 'BARCODE',
}

export enum DepreciationMethod {
  STRAIGHT_LINE = 'STRAIGHT_LINE',
  DECLINING_BALANCE = 'DECLINING_BALANCE',
  UNITS_OF_PRODUCTION = 'UNITS_OF_PRODUCTION',
}

export enum DepreciationRecordStatus {
  CALCULATED = 'CALCULATED',
  REVIEWED = 'REVIEWED',
  POSTED = 'POSTED',
  REVERSED = 'REVERSED',
}

export enum AccountingEntryType {
  DEPRECIATION = 'DEPRECIATION',
  DISPOSAL = 'DISPOSAL',
  IMPAIRMENT = 'IMPAIRMENT',
  REVALUATION = 'REVALUATION',
  ACQUISITION = 'ACQUISITION',
}

export enum AccountingEntryStatus {
  DRAFT = 'DRAFT',
  APPROVED = 'APPROVED',
  POSTED = 'POSTED',
  REVERSED = 'REVERSED',
}

export enum WorkOrderType {
  PREVENTIVE = 'PREVENTIVE',
  CORRECTIVE = 'CORRECTIVE',
  INSPECTION = 'INSPECTION',
}

export enum WorkOrderStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum Priority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export enum AuditAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  VIEW = 'VIEW',
  EXPORT = 'EXPORT',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
}

export enum UserRole {
  SYSADMIN = 'SYSADMIN',
  ASSET_ADMIN = 'ASSET_ADMIN',
  FIELD_OP = 'FIELD_OP',
  ACCOUNTANT = 'ACCOUNTANT',
  AUDITOR = 'AUDITOR',
  STORE_MGR = 'STORE_MGR',
  TECH = 'TECH',
}

export enum TransferStatus {
  PENDING = 'PENDING',
  IN_TRANSIT = 'IN_TRANSIT',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum TagType {
  ON_METAL = 'ON_METAL',
  STANDARD = 'STANDARD',
  INDUSTRIAL = 'INDUSTRIAL',
}

export enum DisposalReason {
  OBSOLESCENCE = 'OBSOLESCENCE',
  DAMAGE = 'DAMAGE',
  THEFT = 'THEFT',
  SALE = 'SALE',
  DONATION = 'DONATION',
}

// ============================================================
// Interfaces
// ============================================================

export interface IAsset {
  id: string;
  assetCode: string;
  // Identificador en el ERP de origen (el número de activo fijo de SAP, para
  // D1). `assetCode` lo genera AT360; este conserva el del cliente.
  externalCode?: string | null;
  rfidEpc?: string;
  name: string;
  description?: string;
  categoryId: string;
  subcategoryId: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  acquisitionDate: string;
  acquisitionCost: number;
  currency: string;
  exchangeRate?: number;
  supplierId?: string;
  invoiceNumber?: string;
  currentLocationId: string;
  responsibleUserId?: string;
  status: AssetStatus;
  condition: AssetCondition;
  warrantyExpiryDate?: string;
  // jsonb nullable en la entidad (`photo_urls` / `documents_urls`). Estaban en
  // la entidad y en el DTO de alta pero no aquí, y el formulario los leía con
  // un cast a `any`: el tipo compartido mentía sobre lo que la API devuelve.
  photoUrls?: string[];
  documentsUrls?: string[];
  customFields?: Record<string, unknown>;
  notes?: string;
  companyId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface IMovement {
  id: string;
  assetId: string;
  movementType: MovementType;
  fromLocationId?: string;
  toLocationId: string;
  fromResponsibleId?: string;
  toResponsibleId?: string;
  movementDate: string;
  captureMethod: CaptureMethod;
  readerId?: string;
  transferOrderId?: string;
  notes?: string;
  photoEvidenceUrl?: string;
  signatureUrl?: string;
  createdBy: string;
  createdAt: string;
}

export interface IDepreciationConfig {
  id: string;
  assetId: string;
  niifStartDate?: string;
  niifUsefulLifeMonths: number;
  niifMethod: DepreciationMethod;
  niifResidualValue: number;
  niifResidualPct: number;
  fiscalStartDate?: string;
  fiscalUsefulLifeMonths: number;
  fiscalMethod: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
}

export interface IDepreciationRecord {
  id: string;
  assetId: string;
  configId: string;
  periodYear: number;
  periodMonth: number;
  niifBookValueStart: number;
  niifDepreciationAmount: number;
  niifAccumulatedDep: number;
  niifBookValueEnd: number;
  fiscalBookValueStart: number;
  fiscalDepreciationAmount: number;
  fiscalAccumulatedDep: number;
  fiscalBookValueEnd: number;
  temporaryDifference: number;
  deferredTaxRate: number;
  deferredTaxAmount: number;
  status: DepreciationRecordStatus;
  calculatedAt: string;
}

export interface IWorkOrder {
  id: string;
  workOrderCode: string;
  type: WorkOrderType;
  priority: Priority;
  assetId: string;
  description: string;
  assignedTechId?: string;
  scheduledDate?: string;
  startDate?: string;
  endDate?: string;
  status: WorkOrderStatus;
  laborCost: number;
  partsCost: number;
  totalCost: number;
  findings?: string;
  actionsTaken?: string;
  nextMaintenanceDate?: string;
  createdBy: string;
  createdAt: string;
}

export interface IAuditLog {
  id: string;
  timestamp: string;
  userId?: string;
  userEmail?: string;
  userIp?: string;
  action: AuditAction;
  entityType: string;
  entityId?: string;
  oldValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
  sessionId?: string;
  companyId: string;
}

export interface IRfidEvent {
  id: string;
  readerId: string;
  readerLocationId: string;
  epc: string;
  rssi?: number;
  antennaPort: number;
  readCount: number;
  eventType: string;
  processed: boolean;
  assetId?: string;
  movementId?: string;
  timestamp: string;
}

// ============================================================
// API Response Types
// ============================================================

export interface IApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown[];
  };
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface IPaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}
